import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Paper
} from '@mui/material';
import {
  Check as CheckIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { usePageTitle } from '../hooks';

interface Price {
  id: string;
  interval: string;
  amount: number;
  currency: string;
}

interface Plan {
  id: string;
  name: string;
  description: string;
  features: string[];
  prices: Price[];
  visibleProtected?: boolean;
}

interface PlansData {
  plans: Plan[];
}

const PlansPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [billingInterval, setBillingInterval] = useState<string>('year');
  const [subscriptionInfo, setSubscriptionInfo] = useState<any>(null);
  const navigate = useNavigate();
  
  // Set page title
  usePageTitle('Subscription Plans');
  
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [plansData, subData] = await Promise.all([
          apiService.getSubscriptionPlans(),
          apiService.getSubscriptionInfo().catch(() => null)
        ]);
        setPlans(plansData.plans);
        setSubscriptionInfo(subData);
        setError(null);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load plans');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);
  
  const handleBillingIntervalChange = (
    event: React.MouseEvent<HTMLElement>,
    newInterval: string,
  ) => {
    if (newInterval !== null) {
      setBillingInterval(newInterval);
    }
  };
  
  const handleSubscribe = async (priceId: string) => {
    setLoading(true);
    try {
      const response = await apiService.createCheckoutSession(
        priceId,
        `${window.location.origin}/settings?section=billing`,
        `${window.location.origin}/settings/plans`
      );
      
      if (response && response.url) {
        // Redirect to Stripe's checkout
        window.location.href = response.url;
      } else {
        setError('Failed to create checkout session');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create checkout session');
    } finally {
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      setLoading(true);
      const portal = await apiService.createBillingPortalSession(`${window.location.origin}/settings/plans`);
      if (portal?.url) window.location.href = portal.url;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to open billing portal');
    } finally {
      setLoading(false);
    }
  };
  
  const handleBackToSettings = () => {
    navigate('/settings?section=billing');
  };
  
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 0
    }).format(amount);
  };
  
  // Filter plans to those intended for the protected/internal UI
  const visiblePlans = plans.filter((p) => p.visibleProtected !== false);
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <>
      <Box sx={{ mb: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToSettings}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Subscription Plans
          </Typography>
        </Box>
        <Typography variant="subtitle1" color="text.secondary">
          Choose the plan that works best for your team
        </Typography>
      </Box>
      
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 4, 
            py: 2,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Error Loading Plans
          </Typography>
          <Typography variant="body2" paragraph>
            {error}
          </Typography>
          {error.includes('Missing required environment variables') && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                How to fix:
              </Typography>
              <Typography variant="body2" component="div">
                <Box 
                  component="ol" 
                  sx={{ 
                    margin: 0, 
                    paddingLeft: '1.5rem' 
                  }}
                >
                  <li>Check that all required Stripe environment variables are set in <code>/backend/.env</code></li>
                  <li>Ensure your Stripe account has Products and Prices configured</li>
                  <li>Restart the backend server</li>
                  <li>Run <code>node check-env.js</code> in the backend directory to verify your configuration</li>
                </Box>
              </Typography>
            </Box>
          )}
        </Alert>
      )}
      
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
        <Paper elevation={0}>
          <ToggleButtonGroup
            color="primary"
            value={billingInterval}
            exclusive
            onChange={handleBillingIntervalChange}
            aria-label="billing interval"
            sx={{ mb: 4 }}
          >
            <ToggleButton value="month">Monthly</ToggleButton>
            <ToggleButton value="year">Yearly</ToggleButton>
          </ToggleButtonGroup>
        </Paper>
      </Box>
      
      {plans.length === 0 && !loading && !error ? (
        <Alert 
          severity="warning" 
          sx={{ mb: 4 }}
        >
          <Typography variant="subtitle1">
            No subscription plans available
          </Typography>
          <Typography variant="body2">
            Please check your Stripe configuration in the backend environment variables.
          </Typography>
        </Alert>
      ) : (
        <Grid container spacing={4}>
            {visiblePlans.map((plan) => {
            const price = plan.prices.find(p => p.interval === billingInterval);
            
            if (!price) return null;
              const currentPlanId = (subscriptionInfo?.plan || '').toLowerCase();
              const isCurrent = currentPlanId === plan.id;
            
            return (
              <Grid item xs={12} md={6} key={plan.id}>
                <Card 
                  elevation={3}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    ...(plan.id === 'pro' ? {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                      borderStyle: 'solid'
                    } : {})
                  }}
                >
                  <CardHeader
                    title={plan.name}
                    titleTypographyProps={{ align: 'center', variant: 'h5' }}
                    sx={(theme) => ({
                      backgroundColor: plan.id === 'pro' 
                        ? theme.palette.primary.main 
                        : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : theme.palette.grey[100]),
                      color: plan.id === 'pro' ? '#fff' : theme.palette.text.primary,
                    })}
                  />
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      {(() => {
                        const monthlyPrice = plans.find(p => p.id === plan.id)?.prices.find(p => p.interval === 'month');
                        const perMonth = billingInterval === 'year' ? Math.ceil(price.amount / 12) : price.amount;
                        return (
                          <>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1 }}>
                              <Typography component="h2" variant="h3">
                                {formatCurrency(perMonth, price.currency)}
                              </Typography>
                              {billingInterval === 'year' && monthlyPrice && (
                                <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                  {formatCurrency(monthlyPrice.amount, price.currency)}
                                </Typography>
                              )}
                            </Box>
                            <Typography variant="h6" color="text.secondary">
                              per month
                            </Typography>
                            {billingInterval === 'year' && monthlyPrice && (
                              <>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  Billed annually at {formatCurrency(price.amount, price.currency)} / year
                                </Typography>
                                {(() => {
                                  const fullYearlyPrice = monthlyPrice.amount * 12;
                                  const savings = Math.round((1 - price.amount / fullYearlyPrice) * 100);
                                  return (
                                    <Typography variant="body2" color="success.main" sx={{ mt: 0.5, fontWeight: 'bold' }}>
                                      Save {savings}%
                                    </Typography>
                                  );
                                })()}
                              </>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                      {plan.description}
                    </Typography>
                    
                    <Divider sx={{ my: 2 }} />
                    
                     <List sx={{ flexGrow: 1 }}>
                       {plan.features.map((feature, index) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                    
                    {isCurrent ? (
                      <>
                        <Button fullWidth variant="outlined" disabled sx={{ mt: 2 }}>
                          Current plan
                        </Button>
                        {subscriptionInfo?.hasSubscription && (
                          <Button fullWidth variant="text" sx={{ mt: 1 }} onClick={handleManageBilling}>
                            Manage billing
                          </Button>
                        )}
                      </>
                    ) : subscriptionInfo?.hasSubscription ? (
                      <Button fullWidth variant={plan.id === 'pro' ? 'contained' : 'outlined'} color="primary" sx={{ mt: 2 }} onClick={handleManageBilling} disabled={loading}>
                        Manage in billing portal
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        variant={plan.id === 'pro' ? "contained" : "outlined"}
                        color="primary"
                        sx={{ mt: 2 }}
                        onClick={() => handleSubscribe(price.id)}
                        disabled={loading}
                      >
                        {loading ? <CircularProgress size={24} /> : 'Subscribe'}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
      
      <Box sx={{ mt: 6, mb: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          For custom plans or enterprise options, please contact our sales team.
        </Typography>
      </Box>
    </>
  );
};

export default PlansPage; 
